import { UserInitiator } from '@/models/FundLevel';

import { createAction, createAsyncAction } from 'typesafe-actions';

import { MODULE } from './constants';
import type {
  ApprovedTradeRequestGroupingPayload,
  BulkTradeNotificationsPayload,
  DealNameHedgeResponseData,
  DownloadDocumentForFundPayload,
  DownloadExecutedTradesReportRequestPayload,
  DownloadExecutionReportRequestPayload,
  HideSelectedTradesPayload,
  InsertClientApprovedStatusPayload,
  InsertPMCRMApprovedStatusPayload,
  InsertSentForExecutionStatusPayload,
  NewTradeRequestStatusPayload,
  RemoveTradeRequestGroupingPayload,
  TradeNotificationState,
  TransactionFilesPostRequestPayload,
  TransactionFilesRequestPayload,
  UpdateTradeRequestPayload,
  UpdateTradeRequestResponse,
  UpdateTradeRequestStatusPayload,
} from './types';

export interface UploadTradeRequestMeta {
  trades_submitted_by: string;
}

export const getFundCompanies = createAsyncAction(
  `${MODULE}/getFundCompanies/request`,
  `${MODULE}/getFundCompanies/success`,
  `${MODULE}/getFundCompanies/failure`,
)<void, { data: TradeNotificationState['fundCompanies']['data'] }, void>();

export const getClassificationTags = createAsyncAction(
  `${MODULE}/getClassificationTags/request`,
  `${MODULE}/getClassificationTags/success`,
  `${MODULE}/getClassificationTags/failure`,
)<void, { data: TradeNotificationState['classificationTags']['data'] }, void>();

export const getUserInitiators = createAsyncAction(
  `${MODULE}/getUserInitiators/request`,
  `${MODULE}/getUserInitiators/success`,
  `${MODULE}/getUserInitiators/failure`,
)<void, { data: TradeNotificationState['userInitiators']['data'] }, void>();

export const getApprovalUsers = createAsyncAction(
  `${MODULE}/getApprovalUsers/request`,
  `${MODULE}/getApprovalUsers/success`,
  `${MODULE}/getApprovalUsers/failure`,
)<void, { data: TradeNotificationState['approvalUsers']['data'] }, void>();

export const getLegalEntities = createAsyncAction(
  `${MODULE}/getLegalEntities/request`,
  `${MODULE}/getLegalEntities/success`,
  `${MODULE}/getLegalEntities/failure`,
)<void, { data: TradeNotificationState['legalEntities']['data'] }, void>();

export const getApprovalTradeRequests = createAsyncAction(
  `${MODULE}/getApprovalTradeRequests/request`,
  `${MODULE}/getApprovalTradeRequests/success`,
  `${MODULE}/getApprovalTradeRequests/failure`,
)<void, { data: TradeNotificationState['approval']['data'] }, void>();

export const getExecutableTradeRequests = createAsyncAction(
  `${MODULE}/getExecutableTradeRequests/request`,
  `${MODULE}/getExecutableTradeRequests/success`,
  `${MODULE}/getExecutableTradeRequests/failure`,
)<void, { data: TradeNotificationState['executable']['data'] }, void>();

export const getPostExecutionTradeRequests = createAsyncAction(
  `${MODULE}/getPostExecutionTradeRequests/request`,
  `${MODULE}/getPostExecutionTradeRequests/success`,
  `${MODULE}/getPostExecutionTradeRequests/failure`,
)<void, { data: TradeNotificationState['postExecution']['data'] }, void>();

export const getCancelledTradeRequests = createAsyncAction(
  `${MODULE}/getCancelledTradeRequests/request`,
  `${MODULE}/getCancelledTradeRequests/success`,
  `${MODULE}/getCancelledTradeRequests/failure`,
)<void, { data: TradeNotificationState['cancelled']['data'] }, void>();

export const getExecutedDataMismatchTradeRequests = createAsyncAction(
  `${MODULE}/getExecutedDataMismatchTradeRequests/request`,
  `${MODULE}/getExecutedDataMismatchTradeRequests/success`,
  `${MODULE}/getExecutedDataMismatchTradeRequests/failure`,
)<void, { data: TradeNotificationState['mismatch']['data'] }, void>();

export const getExecutedTradeRequests = createAsyncAction(
  `${MODULE}/getExecutedTradeRequests/request`,
  `${MODULE}/getExecutedTradeRequests/success`,
  `${MODULE}/getExecutedTradeRequests/failure`,
)<void, { data: TradeNotificationState['executed']['data'] }, void>();

export const getWorkflowStats = createAsyncAction(
  `${MODULE}/getWorkflowStats/request`,
  `${MODULE}/getWorkflowStats/success`,
  `${MODULE}/getWorkflowStats/failure`,
)<void, { data: TradeNotificationState['workflowStats']['data'] }, void>();

export const getFixReferenceData = createAsyncAction(
  `${MODULE}/getFixReferenceData/request`,
  `${MODULE}/getFixReferenceData/success`,
  `${MODULE}/getFixReferenceData/failure`,
)<void, { data: TradeNotificationState['fixReference']['data'] }, void>();

export const checkFixConnectionActions = createAsyncAction(
  `${MODULE}/checkFixConnection/request`,
  `${MODULE}/checkFixConnection/success`,
  `${MODULE}/checkFixConnection/failure`,
)<void, { data: string }, { error: string }>();

export const uploadTradeFile = createAsyncAction(
  `${MODULE}/uploadTradeFile/request`,
  `${MODULE}/uploadTradeFile/success`,
  `${MODULE}/uploadTradeFile/failure`,
)<UploadTradeRequestMeta, { data: any }, { error: string }>();
export const resetUpload = createAction(`${MODULE}/upload/reset`)();
export const resetDownload = createAction(`${MODULE}/download/reset`)();

export const downloadTemplate = createAsyncAction(
  `${MODULE}/downloadTemplate/request`,
  `${MODULE}/downloadTemplate/success`,
  `${MODULE}/downloadTemplate/failure`,
)<DownloadDocumentForFundPayload, void, { error: string }>();

export const downloadExecutionReport = createAsyncAction(
  `${MODULE}/downloadExecutionReport/request`,
  `${MODULE}/downloadExecutionReport/success`,
  `${MODULE}/downloadExecutionReport/failure`,
)<DownloadExecutionReportRequestPayload, void, { error: string }>();

export const downloadExecutedTradesReport = createAsyncAction(
  `${MODULE}/downloadExecutedTradesReport/request`,
  `${MODULE}/downloadExecutedTradesReport/success`,
  `${MODULE}/downloadExecutedTradesReport/failure`,
)<DownloadExecutedTradesReportRequestPayload, void, { error: string }>();

export const downloadTradesforAcceptanceReport = createAsyncAction(
  `${MODULE}/downloadTradesforAcceptanceReport/request`,
  `${MODULE}/downloadTradesforAcceptanceReport/success`,
  `${MODULE}/downloadTradesforAcceptanceReport/failure`,
)<DownloadExecutionReportRequestPayload, void, { error: string }>();

export const createTradeNotificationActions = createAsyncAction(
  `${MODULE}/createTradeNotification/request`,
  `${MODULE}/createTradeNotification/success`,
  `${MODULE}/createTradeNotification/failure`,
)<BulkTradeNotificationsPayload, void, { error: string }>();

export const insertSentForExecutionStatusActions = createAsyncAction(
  `${MODULE}/insertSentForExecutionStatus/request`,
  `${MODULE}/insertSentForExecutionStatus/success`,
  `${MODULE}/insertSentForExecutionStatus/failure`,
)<InsertSentForExecutionStatusPayload, void, { error: string }>();

export const insertClientApprovedStatusActions = createAsyncAction(
  `${MODULE}/insertClientApprovedStatus/request`,
  `${MODULE}/insertClientApprovedStatus/success`,
  `${MODULE}/insertClientApprovedStatus/failure`,
)<InsertClientApprovedStatusPayload, void, { error: string }>();

export const insertTradeRequestStatusActions = createAsyncAction(
  `${MODULE}/insertTradeRequestStatus/request`,
  `${MODULE}/insertTradeRequestStatus/success`,
  `${MODULE}/insertTradeRequestStatus/failure`,
)<NewTradeRequestStatusPayload, void, { error: string }>();

export const insertPMCRMApprovedStatusActions = createAsyncAction(
  `${MODULE}/insertPMCRMApprovedStatus/request`,
  `${MODULE}/insertPMCRMApprovedStatus/success`,
  `${MODULE}/insertPMCRMApprovedStatus/failure`,
)<InsertPMCRMApprovedStatusPayload, void, { error: string }>();

export const updateTradeRequestsActions = createAsyncAction(
  `${MODULE}/updateTradeRequests/request`,
  `${MODULE}/updateTradeRequests/success`,
  `${MODULE}/updateTradeRequests/failure`,
)<UpdateTradeRequestPayload, UpdateTradeRequestResponse, { error: string }>();

export const approvedTradeRequestsGroupingActions = createAsyncAction(
  `${MODULE}/approvedTradeRequestsGrouping/request`,
  `${MODULE}/approvedTradeRequestsGrouping/success`,
  `${MODULE}/approvedTradeRequestsGrouping/failure`,
)<ApprovedTradeRequestGroupingPayload, string, { error: string }>();

export const removeTradeRequestsGroupingActions = createAsyncAction(
  `${MODULE}/removeTradeRequestsGrouping/request`,
  `${MODULE}/removeTradeRequestsGrouping/success`,
  `${MODULE}/removeTradeRequestsGrouping/failure`,
)<RemoveTradeRequestGroupingPayload, string, { error: string }>();

export const hideSelectedTradesActions = createAsyncAction(
  `${MODULE}/hideSelectedTrades/request`,
  `${MODULE}/hideSelectedTrades/success`,
  `${MODULE}/hideSelectedTrades/failure`,
)<HideSelectedTradesPayload, string, { error: string }>();

export const fetchFieldExclusionsActions = createAsyncAction(
  `${MODULE}/fetchFieldExclusions/request`,
  `${MODULE}/fetchFieldExclusions/success`,
  `${MODULE}/fetchFieldExclusions/failure`,
)<void, { data: any }, { error: string }>();

export const createUserInitiatorActions = createAsyncAction(
  `${MODULE}/createUserInitiator/request`,
  `${MODULE}/createUserInitiator/success`,
  `${MODULE}/createUserInitiator/failure`,
)<UserInitiator, UserInitiator, { error: string }>();

export const updateTradeRequestStatusActions = createAsyncAction(
  `${MODULE}/updateTradeRequestStatus/request`,
  `${MODULE}/updateTradeRequestStatus/success`,
  `${MODULE}/updateTradeRequestStatus/failure`,
)<UpdateTradeRequestStatusPayload, any, { error: string }>();

export const submitTransactionFilesActions = createAsyncAction(
  `${MODULE}/submitTransactionFiles/request`,
  `${MODULE}/submitTransactionFiles/success`,
  `${MODULE}/submitTransactionFiles/failure`,
)<TransactionFilesRequestPayload, void, { error: string }>();

export const submitTransactionFilesForTransactionActions = createAsyncAction(
  `${MODULE}/submitTransactionFilesForTransaction/request`,
  `${MODULE}/submitTransactionFilesForTransaction/success`,
  `${MODULE}/submitTransactionFilesForTransaction/failure`,
)<TransactionFilesPostRequestPayload, void, { error: string }>();

export const fetchDocumentsForTradeActions = createAsyncAction(
  `${MODULE}/fetchDocumentsForTrade/request`,
  `${MODULE}/fetchDocumentsForTrade/success`,
  `${MODULE}/fetchDocumentsForTrade/failure`,
)<
  void,
  { data: TradeNotificationState['tradeDocuments']['data'] },
  { error: string }
>();

export const getDealNameHedgeDataActions = createAsyncAction(
  `${MODULE}/getDealNameHedgeData/request`,
  `${MODULE}/getDealNameHedgeData/success`,
  `${MODULE}/getDealNameHedgeData/failure`,
)<void, DealNameHedgeResponseData, { error: string }>();

export const getFileteredFundCompanies = createAsyncAction(
  `${MODULE}/getFileteredFundCompanies/request`,
  `${MODULE}/getFileteredFundCompanies/success`,
  `${MODULE}/getFileteredFundCompanies/failure`,
)<void, { data: TradeNotificationState['fundCompanies']['data'] }, void>();

export const getFilteredClassificationTags = createAsyncAction(
  `${MODULE}/getFilteredClassificationTags/request`,
  `${MODULE}/getFilteredClassificationTags/success`,
  `${MODULE}/getFilteredClassificationTags/failure`,
)<void, { data: TradeNotificationState['classificationTags']['data'] }, void>();
